import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import { useToast } from '../contexts/ToastContext';
import { FiUser, FiMail, FiPhone, FiUserCheck, FiTag } from 'react-icons/fi';
import apiClient, { endpoints } from '../api/client';

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'to_be_confirmed',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const { showSuccess, showError } = useToast();

  const roles = [
    { value: 'confirmed_instructor', label: 'Confirmed Instructor' },
    { value: 'to_be_confirmed', label: 'To Be Confirmed' },
    { value: 'client_in_georgia', label: 'Client In Georgia' },
    { value: 'client_out_georgia', label: 'Client Out Georgia' }
  ];

  // Fetch available tags for the modal
  const fetchAvailableTags = async () => {
    try {
      const params = new URLSearchParams({
        limit: 100, // Get all tags for selection
      });

      const response = await apiClient.get(`/api/tags?${params}`);
      if (response.data.success) {
        setAvailableTags(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
      showError('Failed to load available tags');
    }
  };

  useEffect(() => {
    fetchAvailableTags();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{9,14}$/.test(formData.phone.replace(/[\s-()]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Check authentication state before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      // Step 1: Create user without tags (backend doesn't handle tags in user creation)
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone.replace(/[\s-()]/g, ''), // Clean phone number
        role: formData.role
        // Note: tags are handled separately after user creation
      };

      console.log('Creating user with data:', userData);
      const response = await apiClient.post('/api/users', userData);
      console.log('User creation response:', response);
      
      if (response.data.success) {
        const createdUser = response.data.data.user;
        
        // Step 2: Add tags if any were selected
        if (formData.tags.length > 0) {
          try {
            console.log('Adding tags to user:', formData.tags);
            // Add tags to the user via the user-tags endpoint
            for (const tag of formData.tags) {
              await apiClient.post(endpoints.users.tags(createdUser.id), { tag_name: tag.name });
            }
            console.log('Tags added successfully');
          } catch (tagError) {
            console.warn('Failed to add some tags:', tagError);
            // Don't fail the entire operation if tags fail
            showSuccess('User created successfully, but some tags could not be added.');
          }
        }
        
        showSuccess('User created successfully!');
        onUserCreated(response.data.data);
        handleClose();
      } else {
        showError(response.data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else if (error.response?.status === 401) {
        showError('Authentication failed. Please log in again.');
        // Clear auth state and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        showError('You do not have permission to create users.');
      } else if (error.response?.status === 409) {
        showError('A user with this email already exists');
      } else if (error.response?.status === 400) {
        showError('Invalid user data. Please check all fields and try again.');
      } else if (error.response?.status >= 500) {
        showError('Server error. Please try again later.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        showError('Network error. Please check your connection and try again.');
      } else {
        showError('Failed to create user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'to_be_confirmed',
      tags: []
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New User" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
              <FiUser className="inline h-4 w-4 mr-1" />
              First Name *
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.first_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter first name"
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
            )}
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
              <FiUser className="inline h-4 w-4 mr-1" />
              Last Name *
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.last_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter last name"
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            <FiMail className="inline h-4 w-4 mr-1" />
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter email address"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            <FiPhone className="inline h-4 w-4 mr-1" />
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.phone ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter phone number"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
        </div>

        {/* Role */}
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
            <FiUserCheck className="inline h-4 w-4 mr-1" />
            Role
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            className="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {roles.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiTag className="inline h-4 w-4 mr-1" />
            Tags
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 btn-animate ${
                  formData.tags.find((item) => tag.name === item.name)
                    ? 'bg-primary-100 text-primary-800 border-primary-200'
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                } border`}
              >
                {tag.name}
              </button>
            ))}
          </div>
          {formData.tags.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              Selected: {formData.tags.map((item) => (item.name)).join(', ')}
            </p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 btn-animate"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 btn-animate"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Creating...
              </>
            ) : (
              <>
                <FiUser className="-ml-1 mr-2 h-4 w-4 inline" />
                Add User
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserModal;